import { TableOutlined } from '@ant-design/icons';
import AppSelect from '@nxsys/components/AppSelect';
import {
  AgencyTimesheetValueAccessEnum,
  enumMarginMethod,
} from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { defaultMarginMethod } from '@nxsys/utils';
import { isTableMarginMethod } from '@nxsys/utils/margin';
import { Form, type FormInstance } from 'antd';
import { useState } from 'react';
import MarginTableModal from '../../../agencies/components/MarginTableModal';

type MarginMethodOption = {
  label: string;
  value: string;
};

type MarginMethodFieldProps = {
  form: FormInstance;
  value?: MarginMethodOption;
  employeeId?: string | null;
};

/**
 * Margin Method select for an employee, plus the button that opens the margin
 * band table when a table-based method is selected. Mirrors the agency-level
 * field in modules/agencies/components/payroll-finance-sections/MarginSection.
 */
const MarginMethodField = ({
  form,
  value,
  employeeId,
}: MarginMethodFieldProps) => {
  const { messages } = useTypedIntl();
  const [isTableOpen, setIsTableOpen] = useState(false);
  const toggleTable = () => setIsTableOpen((open) => !open);

  const isTableMethod = isTableMarginMethod(value?.value);
  const canOpenTable = isTableMethod && Boolean(employeeId);

  const handleChange = (option?: MarginMethodOption) => {
    form.setFieldValue('marginMethod', option);
    // The band table supplies the amount, so a leftover flat margin would be
    // stored but never used when payroll is calculated.
    if (isTableMarginMethod(option?.value)) {
      form.setFieldValue('margin', null);
    }
  };

  return (
    <Form.Item label={messages['common.marginMethod']} name="marginMethod">
      <div className="flex items-center gap-[8px]">
        <AppSelect
          options={defaultMarginMethod}
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
          value={value}
          onChange={handleChange}
        />
        {canOpenTable && (
          <TableOutlined className="cursor-pointer" onClick={toggleTable} />
        )}
      </div>
      {canOpenTable && (
        <MarginTableModal
          title={
            defaultMarginMethod.find((item) => item.value === value?.value)
              ?.label ?? ''
          }
          isOpen={isTableOpen}
          toggle={toggleTable}
          subcontractorId={employeeId as string}
          accessType={
            value?.value === enumMarginMethod.PERCENT_TABLE_ON_TIMESHEET_VALUE
              ? AgencyTimesheetValueAccessEnum.PERCENT
              : AgencyTimesheetValueAccessEnum.AMOUNT
          }
        />
      )}
    </Form.Item>
  );
};

export default MarginMethodField;
